import type { ChangeEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { type LocaleTag, locales, resolveLocale } from '@/locales';

import { changeLocale } from './I18nProvider';

/**
 * The language control — which renders NOTHING while the registry holds one locale
 * (ADR-0018).
 *
 * That is the entire behaviour worth describing. The legacy build drew a full dropdown for
 * exactly one language (`i18n.ts:7-9`): a control that cannot change anything, occupying
 * space in the header and promising a choice that does not exist. The i18n plumbing exists
 * from day one because SPEC §7 requires it; the WIDGET appears the moment a second locale
 * is added to `src/locales/index.ts`, with no edit here and none in the header.
 */
export function LanguageSelector(): ReactNode {
  const { t, i18n } = useTranslation();

  // Not `locales.length === 1`: a registry emptied by a bad edit must also render nothing
  // rather than an empty select.
  if (locales.length <= 1) return null;

  const active = resolveLocale(i18n.resolvedLanguage ?? i18n.language);

  const onChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    void changeLocale(i18n, event.target.value as LocaleTag);
  };

  return (
    <select
      className="lang"
      value={active.tag}
      onChange={onChange}
      title={t('action.language')}
      aria-label={t('a11y.language', { language: active.label })}
    >
      {locales.map((locale) => (
        <option key={locale.tag} value={locale.tag} lang={locale.htmlLang}>
          {locale.label}
        </option>
      ))}
    </select>
  );
}
